import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus } from 'lucide-react';
import type { AdminRow, AdminSetupLink } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { formatDate, timeAgo } from '../../lib/format';
import { Alert, Button, Dialog, Field, LoadingBlock } from '../../components/ui';
import { Flag } from './AdminAccountsPage';

export function AdminAdminsPage() {
  const qc = useQueryClient();
  const key = ['admin', 'admins'];
  const list = useQuery({ queryKey: key, queryFn: () => api.get<AdminRow[]>('/admin/admins') });
  const [adding, setAdding] = useState(false);
  const [link, setLink] = useState<AdminSetupLink | null>(null);
  const [removing, setRemoving] = useState<AdminRow | null>(null);
  const [resetting, setResetting] = useState<AdminRow | null>(null);

  const reset = useMutation({
    mutationFn: (a: AdminRow) => api.post<AdminSetupLink>(`/admin/admins/${a.id}/reset`),
    onSuccess: (r) => {
      setResetting(null);
      setLink(r);
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const remove = useMutation({
    mutationFn: (a: AdminRow) => api.del(`/admin/admins/${a.id}`),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  return (
    <>
      <div className="mb-5 flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[24px] font-semibold tracking-[-0.01em]">Admins</h1>
          <p className="text-muted">People who can use this console. Everyone here can do everything, including adding and removing admins.</p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus className="size-4" aria-hidden /> Add admin
        </Button>
      </div>
      {list.isLoading ? <LoadingBlock /> : !list.data ? <Alert>{errorMessage(list.error)}</Alert> : (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {list.data.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
              <div className="min-w-0 flex-1 basis-[220px]">
                <p className="font-medium">
                  {a.name} {a.isMe && <span className="font-normal text-muted">(you)</span>}
                </p>
                <p className="text-[13px] text-muted">{a.email}</p>
              </div>
              <span className="text-[13px] text-muted">
                {a.ready ? (a.lastSignInAt ? `Signed in ${timeAgo(a.lastSignInAt)}` : 'Ready') : a.setupExpiresAt && new Date(a.setupExpiresAt) > new Date() ? <Flag tone="warn">Setup link sent</Flag> : <Flag tone="danger">Setup link expired</Flag>}
              </span>
              {!a.isMe && (
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setResetting(a)}>
                    {a.ready ? 'Reset sign-in' : 'New setup link'}
                  </Button>
                  <Button variant="ghost" className="btn-ghost text-danger" onClick={() => setRemoving(a)}>
                    Remove
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-[13px] text-muted">Lost your own phone? Ask another admin to reset your sign-in, or run <code className="rounded bg-sunken px-1">hoopify reset-admin you@example.com</code> on the server.</p>

      <AddAdminDialog open={adding} onClose={() => setAdding(false)} onAdded={(r) => { setAdding(false); setLink(r); void qc.invalidateQueries({ queryKey: key }); }} />

      <Dialog open={!!resetting} onClose={() => { reset.reset(); setResetting(null); }} title={resetting?.ready ? `Reset sign-in for ${resetting.name}?` : `Send ${resetting?.name} a new setup link?`}>
        <p className="text-muted">
          {resetting?.ready ? 'Their password and two-step sign-in are cleared and they’re signed out. They get an email with a link to set both up again. Only do this once you’re sure it’s really them asking.' : 'The old link stops working.'}
        </p>
        {reset.isError && <div className="mt-3"><Alert>{errorMessage(reset.error)}</Alert></div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setResetting(null)}>Cancel</Button>
          <Button variant="danger" loading={reset.isPending} onClick={() => resetting && reset.mutate(resetting)}>{resetting?.ready ? 'Reset sign-in' : 'Send new link'}</Button>
        </div>
      </Dialog>

      <Dialog open={!!removing} onClose={() => { remove.reset(); setRemoving(null); }} title={`Remove ${removing?.name}?`}>
        <p className="text-muted">They’ll be signed out and can’t use the console any more. Their past actions stay in the admin log.</p>
        {remove.isError && <div className="mt-3"><Alert>{errorMessage(remove.error)}</Alert></div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setRemoving(null)}>Cancel</Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => removing && remove.mutate(removing)}>Remove admin</Button>
        </div>
      </Dialog>

      <Dialog open={!!link} onClose={() => setLink(null)} title="Setup link sent">
        <p className="text-muted">We’ve emailed this to {link?.admin.email}. You can also send it yourself. It works once, for 24 hours.</p>
        <code className="mt-3 block rounded-md bg-sunken px-3 py-2 text-[13px] break-all select-all">{link?.setupUrl}</code>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => link && navigator.clipboard?.writeText(link.setupUrl).catch(() => {})}>
            <Copy className="size-4" aria-hidden /> Copy link
          </Button>
          <Button onClick={() => setLink(null)}>Done</Button>
        </div>
      </Dialog>
    </>
  );
}

function AddAdminDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (r: AdminSetupLink) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: () => api.post<AdminSetupLink>('/admin/admins', { email, name }),
    onSuccess: (r) => {
      setEmail('');
      setName('');
      onAdded(r);
    },
  });
  const fields = fieldErrors(add.error);
  return (
    <Dialog open={open} onClose={() => { add.reset(); onClose(); }} title="Add an admin">
      <form onSubmit={(e) => { e.preventDefault(); add.mutate(); }} className="space-y-4" noValidate>
        <p className="text-muted">They’ll get an email with a link to choose a password and set up two-step sign-in. Admin accounts are separate from coach accounts.</p>
        {add.isError && !Object.keys(fields).length && <Alert>{errorMessage(add.error)}</Alert>}
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} error={fields.name} autoComplete="off" />
        <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={fields.email} autoComplete="off" />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={add.isPending}>Add admin</Button>
        </div>
      </form>
    </Dialog>
  );
}
